import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { adminSuppliersService } from '../../../services/supabaseService';
import { supabase } from '../../../lib/supabase';

const SuppliersTab = ({ language }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    nameEn: '',
    nameZh: '',
    countryId: '',
    descriptionEn: '',
    descriptionZh: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    logoUrl: '',
    isVerified: false
  });

  useEffect(() => {
    loadSuppliers();
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      const { data } = await supabase?.from('countries')?.select('*')?.eq('is_active', true)?.order('name_en');
      setCountries(data || []);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const data = await adminSuppliersService?.getAll();
      setSuppliers(data);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = suppliers?.filter((supplier) => {
    const matchesSearch = supplier?.nameEn?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    supplier?.nameZh?.includes(searchTerm) ||
    supplier?.contactEmail?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCountry = filterCountry === 'all' || supplier?.country?.nameEn === filterCountry;
    return matchesSearch && matchesCountry;
  });

  const handleDelete = async (id) => {
    if (window.confirm(language === 'en' ? 'Are you sure you want to delete this supplier? This action cannot be undone.' : '确定要删除此供应商吗？此操作无法撤销。')) {
      try {
        await adminSuppliersService?.delete(id);
        setSuppliers(suppliers?.filter((s) => s?.id !== id));
        alert(language === 'en' ? 'Supplier deleted successfully' : '供应商删除成功');
      } catch (error) {
        console.error('Failed to delete supplier:', error);
        alert(language === 'en' ? `Failed to delete supplier: ${error?.message}` : `删除失败: ${error?.message}`);
      }
    }
  };

  const handleToggleVerified = async (id, currentStatus) => {
    try {
      await adminSuppliersService?.update(id, { isVerified: !currentStatus });
      setSuppliers(suppliers?.map((s) => s?.id === id ? { ...s, isVerified: !currentStatus } : s));
      alert(language === 'en' ? 'Verification status updated successfully' : '验证状态更新成功');
    } catch (error) {
      console.error('Failed to update verification status:', error);
      alert(language === 'en' ? `Failed to update status: ${error?.message}` : `更新状态失败: ${error?.message}`);
    }
  };

  const uniqueCountries = [...new Set(suppliers?.map(s => s?.country?.nameEn))]?.filter(Boolean);

  const handleOpenAddModal = () => {
    setEditingSupplier(null);
    setFormData({
      nameEn: '',
      nameZh: '',
      countryId: '',
      descriptionEn: '',
      descriptionZh: '',
      contactEmail: '',
      contactPhone: '',
      website: '',
      logoUrl: '',
      isVerified: false
    });
    setShowAddModal(true);
  };

  const handleOpenEditModal = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      nameEn: supplier?.nameEn || '',
      nameZh: supplier?.nameZh || '',
      countryId: supplier?.country?.id || '',
      descriptionEn: supplier?.descriptionEn || '',
      descriptionZh: supplier?.descriptionZh || '',
      contactEmail: supplier?.contactEmail || '',
      contactPhone: supplier?.contactPhone || '',
      website: supplier?.website || '',
      logoUrl: supplier?.logoUrl || '',
      isVerified: supplier?.isVerified || false
    });
    setShowAddModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmitForm = async (e) => {
    e?.preventDefault();
    
    // Validate form data
    if (!formData?.nameEn?.trim() || !formData?.nameZh?.trim()) {
      alert(language === 'en' ? 'Supplier name (English and Chinese) is required' : '供应商名称（英文和中文）为必填项');
      return;
    }
    if (!formData?.countryId) {
      alert(language === 'en' ? 'Please select a country' : '请选择国家');
      return;
    }
    if (!formData?.contactEmail?.trim()) {
      alert(language === 'en' ? 'Contact email is required' : '联系邮箱为必填项');
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.contactEmail?.trim())) {
      alert(language === 'en' ? 'Please enter a valid email address' : '请输入有效的邮箱地址');
      return;
    }
    if (!formData?.contactPhone?.trim()) {
      alert(language === 'en' ? 'Contact phone is required' : '联系电话为必填项');
      return;
    }

    try {
      const supplierData = {
        name_en: formData?.nameEn?.trim(),
        name_zh: formData?.nameZh?.trim(),
        country_id: formData?.countryId,
        description_en: formData?.descriptionEn?.trim() || null,
        description_zh: formData?.descriptionZh?.trim() || null,
        contact_email: formData?.contactEmail?.trim(),
        contact_phone: formData?.contactPhone?.trim(),
        website: formData?.website?.trim() || null,
        logo_url: formData?.logoUrl?.trim() || null,
        is_verified: formData?.isVerified
      };

      if (editingSupplier) {
        await adminSuppliersService?.update(editingSupplier?.id, supplierData);
      } else {
        await adminSuppliersService?.create(supplierData);
      }

      setShowAddModal(false);
      await loadSuppliers();
      alert(language === 'en' ? 'Supplier saved successfully!' : '供应商保存成功！');
    } catch (error) {
      console.error('Error saving supplier:', error);
      alert(language === 'en' ? `Failed to save supplier: ${error?.message}` : `保存失败: ${error?.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Input
            type="search"
            placeholder={language === 'en' ? 'Search suppliers...' : '搜索供应商...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full sm:w-64" />

          <select
            value={filterCountry}
            onChange={(e) => setFilterCountry(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary">

            <option value="all">{language === 'en' ? 'All Countries' : '所有国家'}</option>
            {uniqueCountries?.map((country) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={handleOpenAddModal}>
          {language === 'en' ? 'Add Supplier' : '添加供应商'}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredSuppliers?.map((supplier) =>
        <div key={supplier?.id} className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-smooth">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 rounded-lg overflow-hidden border border-border flex-shrink-0 bg-muted flex items-center justify-center">
                {supplier?.logoUrl ? (
                  <Image src={supplier?.logoUrl} alt={`${supplier?.nameEn} logo`} className="w-full h-full object-cover" />
                ) : (
                  <Icon name="Building2" size={32} color="var(--color-muted-foreground)" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-foreground truncate">{language === 'en' ? supplier?.nameEn : supplier?.nameZh}</h3>
                  {supplier?.isVerified &&
                <Icon name="BadgeCheck" size={20} color="var(--color-success)" />
                }
                </div>
                <p className="text-sm text-muted-foreground mb-2">{language === 'en' ? supplier?.country?.nameEn : supplier?.country?.nameZh}</p>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                <span className="text-foreground truncate">{supplier?.contactEmail}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Phone" size={16} color="var(--color-muted-foreground)" />
                <span className="text-foreground">{supplier?.contactPhone}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-border">
              <Button
              variant="outline"
              size="sm"
              iconName="Edit2"
              iconPosition="left"
              className="flex-1"
              onClick={() => handleOpenEditModal(supplier)}>
                {language === 'en' ? 'Edit' : '编辑'}
              </Button>
              <Button
              variant={supplier?.isVerified ? 'secondary' : 'default'}
              size="sm"
              iconName={supplier?.isVerified ? 'ShieldOff' : 'Shield'}
              iconPosition="left"
              onClick={() => handleToggleVerified(supplier?.id, supplier?.isVerified)}
              className="flex-1">

                {supplier?.isVerified ? language === 'en' ? 'Unverify' : '取消验证' : language === 'en' ? 'Verify' : '验证'}
              </Button>
              <button
              onClick={() => handleDelete(supplier?.id)}
              className="p-2 rounded-md hover:bg-muted transition-smooth"
              aria-label="Delete supplier">

                <Icon name="Trash2" size={18} color="var(--color-error)" />
              </button>
            </div>
          </div>
        )}
      </div>
      {filteredSuppliers?.length === 0 &&
      <div className="text-center py-12">
          <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">{language === 'en' ? 'No suppliers found' : '未找到供应商'}</p>
        </div>
      }

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm" onClick={() => setShowAddModal(false)}>
          <div className="bg-card border border-border rounded-lg shadow-modal max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-foreground">
                  {editingSupplier ? (language === 'en' ? 'Edit Supplier' : '编辑供应商') : (language === 'en' ? 'Add Supplier' : '添加供应商')}
                </h3>
                <button onClick={() => setShowAddModal(false)} className="p-2 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmitForm} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input label={language === 'en' ? 'Name (English)' : '名称（英文）'} name="nameEn" value={formData?.nameEn} onChange={handleFormChange} required />
                <Input label={language === 'en' ? 'Name (Chinese)' : '名称（中文）'} name="nameZh" value={formData?.nameZh} onChange={handleFormChange} required />
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1">{language === 'en' ? 'Country' : '国家'}</label>
                  <select name="countryId" value={formData?.countryId} onChange={handleFormChange} required className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground">
                    <option value="">{language === 'en' ? 'Select country' : '选择国家'}</option>
                    {countries?.map(c => <option key={c?.id} value={c?.id}>{language === 'en' ? c?.name_en : c?.name_zh}</option>)}
                  </select>
                </div>
                <Input label={language === 'en' ? 'Contact Email' : '联系邮箱'} name="contactEmail" type="email" value={formData?.contactEmail} onChange={handleFormChange} required />
                <Input label={language === 'en' ? 'Contact Phone' : '联系电话'} name="contactPhone" value={formData?.contactPhone} onChange={handleFormChange} required />
                <Input label={language === 'en' ? 'Website' : '网站'} name="website" value={formData?.website} onChange={handleFormChange} placeholder="https://..." />
                <Input label={language === 'en' ? 'Logo URL' : '标志URL'} name="logoUrl" value={formData?.logoUrl} onChange={handleFormChange} placeholder="https://..." />
              </div>
              <Input label={language === 'en' ? 'Description (English)' : '描述（英文）'} name="descriptionEn" value={formData?.descriptionEn} onChange={handleFormChange} />
              <Input label={language === 'en' ? 'Description (Chinese)' : '描述（中文）'} name="descriptionZh" value={formData?.descriptionZh} onChange={handleFormChange} />
              <label className="flex items-center gap-2">
                <input type="checkbox" name="isVerified" checked={formData?.isVerified} onChange={handleFormChange} className="w-4 h-4" />
                <span className="text-sm text-foreground">{language === 'en' ? 'Verified' : '已验证'}</span>
              </label>
              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" variant="default" className="flex-1">{language === 'en' ? 'Save' : '保存'}</Button>
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">{language === 'en' ? 'Cancel' : '取消'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

};

export default SuppliersTab;